import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createCompany } from '../../lib/supabase/companies';
import { supabase } from '../../config/supabase';
import { FiHome, FiMail, FiPhone, FiMapPin, FiGlobe, FiSettings, FiDollarSign, FiUsers, FiCheck, FiX, FiUpload, FiImage } from 'react-icons/fi';

// Move components outside to prevent re-creation on each render
const ErrorDisplay = ({ errors }) => {
    if (errors.length === 0) return null;

    return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h3 className="text-red-800 font-medium mb-2">
                Please fix the following errors:
            </h3>
            <ul className="text-red-700 text-sm space-y-1">
                {errors.map((error, index) => (
                    <li key={index} className="flex items-start">
                        <span className="text-red-500 mr-2">•</span>
                        {error}
                    </li>
                ))}
            </ul>
        </div>
    );
};

const InfoCard = ({ title, children, icon: Icon, color = "blue" }) => (
    <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 text-${color}-500`} />
            <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
        </div>
        <div className="space-y-2">
            {children}
        </div>
    </div>
);

const CheckboxButton = ({ label, isChecked, onClick, className = "" }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium flex items-center gap-2 ${
            isChecked
                ? 'bg-green-500 text-white border-green-500 shadow-md'
                : 'bg-white text-gray-700 border-gray-300 hover:border-green-300 hover:bg-green-50'
        } ${className}`}
    >
        {isChecked ? <FiCheck className="w-4 h-4" /> : <FiX className="w-4 h-4" />}
        {label}
    </button>
);

const SelectionButton = ({ label, isSelected, onClick, className = "" }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium ${
            isSelected
                ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50'
        } ${className}`}
    >
        {label}
    </button>
);

const INITIAL_FORM_STATE = {
    name: '',
    slug: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Australia',
    timezone: 'Australia/Melbourne',
    logo_url: '',
    primary_color: '#3b82f6',
    secondary_color: '#8b5cf6',

    // Subscription & Billing
    subscription_plan: 'free',
    subscription_status: 'trial',
    billing_frequency: 'monthly',
    pricing_model: 'fixed',
    base_price: 0,
    per_user_price: 0,
    setup_fee: 0,
    currency: 'AUD',

    // Limits
    max_users: null,
    max_jobs: null,
    max_storage_gb: null,

    // Features
    features_included: {
        job_management: true,
        user_management: true,
        notifications: true,
        file_storage: true,
        reporting: false,
        custom_branding: false,
        api_access: false,
        integrations: false,
        priority_support: false,
        advanced_analytics: false
    },

    // Billing Info
    billing_email: '',

    // Trial & Subscription Dates
    trial_start_date: '',
    trial_end_date: '',

    // Custom Plan
    is_custom_plan: false,
    custom_plan_description: '',
    custom_pricing_notes: '',
    contract_terms: '',

    // Discount
    discount_percentage: 0,
    discount_amount: 0,

    // Settings
    is_active: true,
    requires_approval: false,

    // Notes
    account_manager_notes: ''
};

const CreateCompany = () => {
    const navigate = useNavigate();
    const { userProfile, company } = useAuth();
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [formErrors, setFormErrors] = useState([]);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    const validateForm = () => {
        const errors = [];

        // Required fields
        if (!formData.name?.trim()) {
            errors.push("Company name is required");
        }

        if (!formData.slug?.trim()) {
            errors.push("Company slug is required");
        } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
            errors.push("Slug must contain only lowercase letters, numbers, and hyphens");
        }

        if (!formData.contact_email?.trim()) {
            errors.push("Contact email is required");
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
            errors.push("Contact email must be a valid email address");
        }

        // Validate color codes
        if (formData.primary_color && !/^#[0-9A-Fa-f]{6}$/.test(formData.primary_color)) {
            errors.push("Primary color must be a valid hex color code (e.g., #3b82f6)");
        }

        if (formData.secondary_color && !/^#[0-9A-Fa-f]{6}$/.test(formData.secondary_color)) {
            errors.push("Secondary color must be a valid hex color code (e.g., #8b5cf6)");
        }

        // Validate pricing
        if (formData.base_price < 0) {
            errors.push("Base price cannot be negative");
        }

        if (formData.per_user_price < 0) {
            errors.push("Per user price cannot be negative");
        }

        if (formData.setup_fee < 0) {
            errors.push("Setup fee cannot be negative");
        }

        if (formData.discount_percentage < 0 || formData.discount_percentage > 100) {
            errors.push("Discount percentage must be between 0 and 100");
        }

        // Validate trial dates
        if (formData.trial_start_date && formData.trial_end_date) {
            const startDate = new Date(formData.trial_start_date);
            const endDate = new Date(formData.trial_end_date);

            if (endDate < startDate) {
                errors.push("Trial end date must be after trial start date");
            }
        }

        return errors;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.includes('.')) {
            handleNestedChange(name, type === 'checkbox' ? checked : value);
            return;
        }

        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleNestedChange = (path, value) => {
        const keys = path.split('.');
        setFormData(prevState => {
            const newState = { ...prevState };
            let current = newState;

            for (let i = 0; i < keys.length - 1; i++) {
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return newState;
        });
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please upload an image file');
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            alert('Image size should be less than 2MB');
            return;
        }

        setLogoFile(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const uploadLogoToStorage = async () => {
        if (!logoFile) return null;

        try {
            setUploadingLogo(true);

            // Generate unique filename
            const fileExt = logoFile.name.split('.').pop();
            const fileName = `${formData.slug || Date.now()}-logo.${fileExt}`;
            const filePath = `company-logos/${fileName}`;

            // Upload to Supabase storage
            const { data, error } = await supabase.storage
                .from('public-images')
                .upload(filePath, logoFile, {
                    cacheControl: '3600',
                    upsert: false
                });

            if (error) throw error;

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('public-images')
                .getPublicUrl(filePath);

            return publicUrl;
        } catch (error) {
            console.error('Error uploading logo:', error);
            throw error;
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (buttonLoading) return;

        const validationErrors = validateForm();
        setFormErrors(validationErrors);

        if (validationErrors.length > 0) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setButtonLoading(true);

        try {
            // Upload logo if file is selected
            let logoUrl = formData.logo_url;
            if (logoFile) {
                logoUrl = await uploadLogoToStorage();
            }

            const result = await createCompany(
                { ...formData, logo_url: logoUrl },
                userProfile.id,
                userProfile.username || userProfile.full_name
            );

            alert("Company created successfully!");
            setFormData({ ...INITIAL_FORM_STATE });
            setFormErrors([]);
            setLogoFile(null);
            setLogoPreview(null);
            navigate(`/${company.slug}/companies`);
        } catch (error) {
            console.error("Error creating company:", error);
            alert("Error creating company: " + error.message);
        } finally {
            setButtonLoading(false);
        }
    };

    const handleCancel = () => {
        if (window.confirm("Are you sure you want to cancel? All unsaved changes will be lost.")) {
            navigate(`/${company.slug}/companies`);
        }
    };

    // Auto-generate slug from name
    const handleNameChange = (e) => {
        const name = e.target.value;

        // Auto-generate slug if it hasn't been manually edited
        const autoSlug = name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();

        // Update both name and slug in a single state update
        setFormData(prev => ({
            ...prev,
            name: name,
            slug: autoSlug
        }));
    };

    return (
        <div className="mobile-management-page mx-auto">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Create New Company</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                    Set up a new company with billing, features, and configuration
                </p>
            </div>

            <ErrorDisplay errors={formErrors} />

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <InfoCard title="Basic Information" icon={FiHome}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Company Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleNameChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="e.g., Acme Corporation"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Slug (URL identifier) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="e.g., acme-corporation"
                                pattern="[a-z0-9-]+"
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Lowercase letters, numbers, and hyphens only
                            </p>
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Company Logo
                            </label>
                            <div className="flex items-start gap-4">
                                {/* Upload Button */}
                                <div className="flex-1">
                                    <label className="flex flex-col items-center justify-center w-full h-32 px-4 border-2 border-gray-300 dark:border-zinc-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-zinc-800 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors">
                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            <FiUpload className="w-8 h-8 mb-2 text-gray-500 dark:text-gray-400" />
                                            <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
                                                <span className="font-semibold">Click to upload</span> or drag and drop
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">PNG, JPG, SVG (MAX. 2MB)</p>
                                        </div>
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleLogoUpload}
                                        />
                                    </label>
                                    {logoFile && (
                                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                            Selected: {logoFile.name}
                                        </p>
                                    )}
                                </div>

                                {/* Preview */}
                                {logoPreview && (
                                    <div className="flex-shrink-0">
                                        <div className="w-32 h-32 border-2 border-gray-300 dark:border-zinc-600 rounded-lg overflow-hidden bg-white dark:bg-zinc-800">
                                            <img
                                                src={logoPreview}
                                                alt="Logo preview"
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setLogoFile(null);
                                                setLogoPreview(null);
                                            }}
                                            className="mt-2 text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Primary Color
                                </label>
                                <input
                                    type="color"
                                    name="primary_color"
                                    value={formData.primary_color}
                                    onChange={handleChange}
                                    className="w-full h-12 border border-gray-300 dark:border-zinc-600 rounded-lg cursor-pointer"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Secondary Color
                                </label>
                                <input
                                    type="color"
                                    name="secondary_color"
                                    value={formData.secondary_color}
                                    onChange={handleChange}
                                    className="w-full h-12 border border-gray-300 dark:border-zinc-600 rounded-lg cursor-pointer"
                                />
                            </div>
                        </div>
                    </div>
                </InfoCard>

                {/* Contact Information */}
                <InfoCard title="Contact Information" icon={FiMail}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Contact Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="contact_email"
                                value={formData.contact_email}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="contact@company.com"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Contact Phone
                            </label>
                            <input
                                type="tel"
                                name="contact_phone"
                                value={formData.contact_phone}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="+61 2 1234 5678"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Billing Email
                            </label>
                            <input
                                type="email"
                                name="billing_email"
                                value={formData.billing_email}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="billing@company.com"
                            />
                        </div>
                    </div>
                </InfoCard>

                {/* Address Information */}
                <InfoCard title="Address Information" icon={FiMapPin}>
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Street Address
                            </label>
                            <input
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="123 Main Street"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    City
                                </label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Melbourne"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    State
                                </label>
                                <input
                                    type="text"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="VIC"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Postal Code
                                </label>
                                <input
                                    type="text"
                                    name="postal_code"
                                    value={formData.postal_code}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="3000"
                                />
                            </div>
                        </div>
                    </div>
                </InfoCard>

                {/* Regional Settings */}
                <InfoCard title="Regional Settings" icon={FiGlobe}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Country
                            </label>
                            <select
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="Australia">Australia</option>
                                <option value="New Zealand">New Zealand</option>
                                <option value="United States">United States</option>
                                <option value="United Kingdom">United Kingdom</option>
                                <option value="Canada">Canada</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Timezone
                            </label>
                            <select
                                name="timezone"
                                value={formData.timezone}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="Australia/Melbourne">Australia/Melbourne</option>
                                <option value="Australia/Sydney">Australia/Sydney</option>
                                <option value="Australia/Brisbane">Australia/Brisbane</option>
                                <option value="Australia/Perth">Australia/Perth</option>
                                <option value="Australia/Adelaide">Australia/Adelaide</option>
                                <option value="Pacific/Auckland">Pacific/Auckland</option>
                                <option value="America/New_York">America/New_York</option>
                                <option value="America/Los_Angeles">America/Los_Angeles</option>
                                <option value="Europe/London">Europe/London</option>
                            </select>
                        </div>
                    </div>
                </InfoCard>

                {/* Subscription & Billing */}
                <InfoCard title="Subscription & Billing" icon={FiDollarSign}>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Subscription Plan
                                </label>
                                <div className="flex flex-col gap-2">
                                    <SelectionButton
                                        label="Free"
                                        isSelected={formData.subscription_plan === 'free'}
                                        onClick={() => handleChange({ target: { name: 'subscription_plan', value: 'free' } })}
                                    />
                                    <SelectionButton
                                        label="Starter"
                                        isSelected={formData.subscription_plan === 'starter'}
                                        onClick={() => handleChange({ target: { name: 'subscription_plan', value: 'starter' } })}
                                    />
                                    <SelectionButton
                                        label="Professional"
                                        isSelected={formData.subscription_plan === 'professional'}
                                        onClick={() => handleChange({ target: { name: 'subscription_plan', value: 'professional' } })}
                                    />
                                    <SelectionButton
                                        label="Enterprise"
                                        isSelected={formData.subscription_plan === 'enterprise'}
                                        onClick={() => handleChange({ target: { name: 'subscription_plan', value: 'enterprise' } })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Subscription Status
                                </label>
                                <div className="flex flex-col gap-2">
                                    <SelectionButton
                                        label="Trial"
                                        isSelected={formData.subscription_status === 'trial'}
                                        onClick={() => handleChange({ target: { name: 'subscription_status', value: 'trial' } })}
                                    />
                                    <SelectionButton
                                        label="Active"
                                        isSelected={formData.subscription_status === 'active'}
                                        onClick={() => handleChange({ target: { name: 'subscription_status', value: 'active' } })}
                                    />
                                    <SelectionButton
                                        label="Suspended"
                                        isSelected={formData.subscription_status === 'suspended'}
                                        onClick={() => handleChange({ target: { name: 'subscription_status', value: 'suspended' } })}
                                    />
                                    <SelectionButton
                                        label="Cancelled"
                                        isSelected={formData.subscription_status === 'cancelled'}
                                        onClick={() => handleChange({ target: { name: 'subscription_status', value: 'cancelled' } })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Billing Frequency
                                </label>
                                <div className="flex flex-col gap-2">
                                    <SelectionButton
                                        label="Monthly"
                                        isSelected={formData.billing_frequency === 'monthly'}
                                        onClick={() => handleChange({ target: { name: 'billing_frequency', value: 'monthly' } })}
                                    />
                                    <SelectionButton
                                        label="Quarterly"
                                        isSelected={formData.billing_frequency === 'quarterly'}
                                        onClick={() => handleChange({ target: { name: 'billing_frequency', value: 'quarterly' } })}
                                    />
                                    <SelectionButton
                                        label="Annually"
                                        isSelected={formData.billing_frequency === 'annually'}
                                        onClick={() => handleChange({ target: { name: 'billing_frequency', value: 'annually' } })}
                                    />
                                    <SelectionButton
                                        label="One-time"
                                        isSelected={formData.billing_frequency === 'one_time'}
                                        onClick={() => handleChange({ target: { name: 'billing_frequency', value: 'one_time' } })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Pricing Model
                                </label>
                                <div className="flex gap-2">
                                    <SelectionButton
                                        label="Fixed"
                                        isSelected={formData.pricing_model === 'fixed'}
                                        onClick={() => handleChange({ target: { name: 'pricing_model', value: 'fixed' } })}
                                        className="flex-1"
                                    />
                                    <SelectionButton
                                        label="Per User"
                                        isSelected={formData.pricing_model === 'per_user'}
                                        onClick={() => handleChange({ target: { name: 'pricing_model', value: 'per_user' } })}
                                        className="flex-1"
                                    />
                                    <SelectionButton
                                        label="Usage-based"
                                        isSelected={formData.pricing_model === 'usage_based'}
                                        onClick={() => handleChange({ target: { name: 'pricing_model', value: 'usage_based' } })}
                                        className="flex-1"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Currency
                                </label>
                                <select
                                    name="currency"
                                    value={formData.currency}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="AUD">AUD - Australian Dollar</option>
                                    <option value="USD">USD - US Dollar</option>
                                    <option value="EUR">EUR - Euro</option>
                                    <option value="GBP">GBP - British Pound</option>
                                    <option value="NZD">NZD - New Zealand Dollar</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Base Price ({formData.currency})
                                </label>
                                <input
                                    type="number"
                                    name="base_price"
                                    value={formData.base_price}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Per User Price ({formData.currency})
                                </label>
                                <input
                                    type="number"
                                    name="per_user_price"
                                    value={formData.per_user_price}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Setup Fee ({formData.currency})
                                </label>
                                <input
                                    type="number"
                                    name="setup_fee"
                                    value={formData.setup_fee}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Discount Percentage (%)
                                </label>
                                <input
                                    type="number"
                                    name="discount_percentage"
                                    value={formData.discount_percentage}
                                    onChange={handleChange}
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Discount Amount ({formData.currency})
                                </label>
                                <input
                                    type="number"
                                    name="discount_amount"
                                    value={formData.discount_amount}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>
                </InfoCard>

                {/* Limits & Quotas */}
                <InfoCard title="Limits & Quotas" icon={FiUsers}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Max Users
                            </label>
                            <input
                                type="number"
                                name="max_users"
                                value={formData.max_users || ''}
                                onChange={handleChange}
                                min="0"
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Unlimited"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Max Jobs
                            </label>
                            <input
                                type="number"
                                name="max_jobs"
                                value={formData.max_jobs || ''}
                                onChange={handleChange}
                                min="0"
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Unlimited"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Max Storage (GB)
                            </label>
                            <input
                                type="number"
                                name="max_storage_gb"
                                value={formData.max_storage_gb || ''}
                                onChange={handleChange}
                                min="0"
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Unlimited"
                            />
                        </div>
                    </div>
                </InfoCard>

                {/* Features */}
                <InfoCard title="Features Included" icon={FiSettings}>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        <CheckboxButton
                            label="Job Management"
                            isChecked={formData.features_included.job_management}
                            onClick={() => handleNestedChange('features_included.job_management', !formData.features_included.job_management)}
                        />
                        <CheckboxButton
                            label="User Management"
                            isChecked={formData.features_included.user_management}
                            onClick={() => handleNestedChange('features_included.user_management', !formData.features_included.user_management)}
                        />
                        <CheckboxButton
                            label="Notifications"
                            isChecked={formData.features_included.notifications}
                            onClick={() => handleNestedChange('features_included.notifications', !formData.features_included.notifications)}
                        />
                        <CheckboxButton
                            label="File Storage"
                            isChecked={formData.features_included.file_storage}
                            onClick={() => handleNestedChange('features_included.file_storage', !formData.features_included.file_storage)}
                        />
                        <CheckboxButton
                            label="Reporting"
                            isChecked={formData.features_included.reporting}
                            onClick={() => handleNestedChange('features_included.reporting', !formData.features_included.reporting)}
                        />
                        <CheckboxButton
                            label="Custom Branding"
                            isChecked={formData.features_included.custom_branding}
                            onClick={() => handleNestedChange('features_included.custom_branding', !formData.features_included.custom_branding)}
                        />
                        <CheckboxButton
                            label="API Access"
                            isChecked={formData.features_included.api_access}
                            onClick={() => handleNestedChange('features_included.api_access', !formData.features_included.api_access)}
                        />
                        <CheckboxButton
                            label="Integrations"
                            isChecked={formData.features_included.integrations}
                            onClick={() => handleNestedChange('features_included.integrations', !formData.features_included.integrations)}
                        />
                        <CheckboxButton
                            label="Priority Support"
                            isChecked={formData.features_included.priority_support}
                            onClick={() => handleNestedChange('features_included.priority_support', !formData.features_included.priority_support)}
                        />
                        <CheckboxButton
                            label="Advanced Analytics"
                            isChecked={formData.features_included.advanced_analytics}
                            onClick={() => handleNestedChange('features_included.advanced_analytics', !formData.features_included.advanced_analytics)}
                        />
                    </div>
                </InfoCard>

                {/* Trial & Dates */}
                <InfoCard title="Trial Period" icon={FiSettings}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Trial Start Date
                            </label>
                            <input
                                type="date"
                                name="trial_start_date"
                                value={formData.trial_start_date}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Trial End Date
                            </label>
                            <input
                                type="date"
                                name="trial_end_date"
                                value={formData.trial_end_date}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                </InfoCard>

                {/* Custom Plan */}
                <InfoCard title="Custom Plan Configuration" icon={FiSettings}>
                    <div className="space-y-4">
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                name="is_custom_plan"
                                checked={formData.is_custom_plan}
                                onChange={handleChange}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                This is a custom plan
                            </label>
                        </div>

                        {formData.is_custom_plan && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Custom Plan Description
                                    </label>
                                    <textarea
                                        name="custom_plan_description"
                                        value={formData.custom_plan_description}
                                        onChange={handleChange}
                                        rows="3"
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Describe the custom plan details..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Custom Pricing Notes
                                    </label>
                                    <textarea
                                        name="custom_pricing_notes"
                                        value={formData.custom_pricing_notes}
                                        onChange={handleChange}
                                        rows="3"
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Special pricing arrangements..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Contract Terms
                                    </label>
                                    <textarea
                                        name="contract_terms"
                                        value={formData.contract_terms}
                                        onChange={handleChange}
                                        rows="3"
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Contract terms and conditions..."
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </InfoCard>

                {/* Account Management */}
                <InfoCard title="Account Management" icon={FiSettings}>
                    <div className="space-y-4">
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Company is active
                                </label>
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    name="requires_approval"
                                    checked={formData.requires_approval}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Requires admin approval for actions
                                </label>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Account Manager Notes
                            </label>
                            <textarea
                                name="account_manager_notes"
                                value={formData.account_manager_notes}
                                onChange={handleChange}
                                rows="4"
                                className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Internal notes about this company account..."
                            />
                        </div>
                    </div>
                </InfoCard>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-zinc-700">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="px-6 py-3 bg-gray-200 dark:bg-zinc-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-zinc-600 transition-colors font-medium"
                        disabled={buttonLoading}
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={buttonLoading}
                        className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {buttonLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Creating...
                            </>
                        ) : (
                            <>
                                <FiCheck className="w-5 h-5" />
                                Create Company
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateCompany;
